import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Autocomplete,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  OutlinedInput,
  CircularProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Brush,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ReferenceArea,
  ReferenceLine
} from 'recharts';
import dayjs from 'dayjs';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ReplayIcon from '@mui/icons-material/Replay';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import SearchIcon from '@mui/icons-material/Search';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Constants
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:17001';
const DEFAULT_CHANNELS = [
  'MAIN.Dicing.MFC[3].FLow_reading',
  'MAIN.Dicing.MFC[2].FLow_reading',
  'MAIN.Dicing.MFC[4].FLow_reading',
  'MAIN.Dicing.MFC[5].FLow_reading',
  'MAIN.Dicing.MFC[6].FLow_reading',
  'MAIN.Dicing.MFC[7].FLow_reading'
];

const QUICK_RANGES = [
  { label: 'Last 5 minutes', value: { start: () => dayjs().subtract(5, 'minute'), end: () => dayjs() } },
  { label: 'Last 15 minutes', value: { start: () => dayjs().subtract(15, 'minute'), end: () => dayjs() } },
  { label: 'Last 1 hour', value: { start: () => dayjs().subtract(1, 'hour'), end: () => dayjs() } },
  { label: 'Last 6 hours', value: { start: () => dayjs().subtract(6, 'hour'), end: () => dayjs() } },
  { label: 'Last 24 hours', value: { start: () => dayjs().subtract(24, 'hour'), end: () => dayjs() } },
  { label: 'Last 7 days', value: { start: () => dayjs().subtract(7, 'day'), end: () => dayjs() } },
  { label: 'Custom', value: null },
];

const REFRESH_INTERVALS = [
  { label: 'Manual', value: 0 },
  { label: 'Auto (1s)', value: 1000 },
  { label: '5s', value: 5000 },
  { label: '10s', value: 10000 },
  { label: '30s', value: 30000 },
  { label: '1m', value: 60000 }
];

// Add after imports, before DashboardProps

type ChartDataPoint = {
  _binned: string;
  _value: number | null;
  _time: any;
  isRecent: boolean;
  _paintTime?: number;
};

interface DashboardProps {
  location: string;
  toolType: string;
  selectedTool: string;
}

// Mock data generators
const generateMockData = (toolName: string) => {
  const data = [];
  const now = dayjs();
  for (let i = 23; i >= 0; i--) {
    const time = now.subtract(i, 'hour');
    data.push({
      time: time.format('HH:mm'),
      temperature: 85 + Math.random() * 10,
      pressure: 2.5 + Math.random() * 0.5,
      flowRate: 160 + Math.random() * 20,
      power: 320 + Math.random() * 30
    });
  }
  return data;
};

const generateAlarmData = () => [
  { name: 'High Temperature', value: 3, color: '#ff6b6b' },
  { name: 'Low Pressure', value: 1, color: '#4ecdc4' },
  { name: 'Flow Rate Error', value: 2, color: '#45b7d1' },
  { name: 'Power Fluctuation', value: 1, color: '#96ceb4' }
];

const generateOeeData = () => [
  { name: 'Availability', value: 95.2 },
  { name: 'Performance', value: 87.8 },
  { name: 'Quality', value: 99.1 },
  { name: 'OEE', value: 82.7 }
];

// 1. Add this at the top of the file (after imports):
const getColorForChannel = (ch: string, idx: number): string => {
  const hue = (idx * 360 / 24) % 360;
  return `hsl(${hue}, 70%, 50%)`;
};

export default function Dashboard({ location, toolType, selectedTool }: DashboardProps) {
  const theme = useTheme();
  
  // State
  const [availableChannels, setAvailableChannels] = useState<string[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [selectedChannels, setSelectedChannels] = useState<string[]>(DEFAULT_CHANNELS);
  const [timeRange, setTimeRange] = useState({ start: 'now-24h', end: 'now' });
  const [from, setFrom] = useState('now-24h');
  const [to, setTo] = useState('now');
  const [quickRangeLabel, setQuickRangeLabel] = useState('Last 24 hours');
  const [refreshInterval, setRefreshInterval] = useState(0);
  const [tableChannel, setTableChannel] = useState('');
  const [customRange, setCustomRange] = useState<{start: string, end: string} | null>(null);
  const [loading, setLoading] = useState(false);
  const [liveMode, setLiveMode] = useState(false);
  const [currentTimeWindow, setCurrentTimeWindow] = useState({
    start: dayjs().subtract(24, 'hour'),
    end: dayjs()
  });
  const [zoomLevel, setZoomLevel] = useState(24);
  const [channelDataMap, setChannelDataMap] = useState<Record<string, any[]>>({});
  const [channelLoadingMap, setChannelLoadingMap] = useState<Record<string, boolean>>({});
  const [channelDataPointCounts, setChannelDataPointCounts] = useState<Record<string, number>>({});
  const [chartZoomRanges, setChartZoomRanges] = useState<Record<string, {start: number, end: number}>>({});
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    DEFAULT_CHANNELS.forEach(ch => {
      initial[ch] = true; // All charts expanded by default
    });
    return initial;
  });
  
  // Track last fetched timestamp for each channel for incremental live updates
  const [lastFetchedTimestamps, setLastFetchedTimestamps] = useState<Record<string, string>>({});
  
  // Individual zoom levels for each channel
  const [channelZoomLevels, setChannelZoomLevels] = useState<Record<string, number>>({});
  const [channelTimeWindows, setChannelTimeWindows] = useState<Record<string, {start: dayjs.Dayjs, end: dayjs.Dayjs}>>({});
  
  // Auto-refresh functionality
  const [refreshTimer, setRefreshTimer] = useState<NodeJS.Timeout | null>(null);
  const [paintTimer, setPaintTimer] = useState<NodeJS.Timeout | null>(null);
  const [lastPaintTime, setLastPaintTime] = useState<number>(dayjs().valueOf());
  const [isLiveMode, setIsLiveMode] = useState(false);
  
  // Add state for custom range picker dialog
  const [customRangeDialogOpen, setCustomRangeDialogOpen] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<[string, string]>(() => {
    const today = dayjs().format('YYYY-MM-DD');
    const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    return [yesterday, today];
  });

  // Add state for channel selector dialog and search
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [channelSearch, setChannelSearch] = useState('');
  
  // Mock data for charts
  const [chartData, setChartData] = useState(generateMockData(selectedTool));
  const [alarmData] = useState(generateAlarmData());
  const [oeeData] = useState(generateOeeData());

  // Add state for drag state
  const [dragState, setDragState] = useState<Record<string, { start: number | null, end: number | null, dragging: boolean }>>({});

  // Add state for previous windows
  const [previousWindows, setPreviousWindows] = useState<Record<string, {start: string, end: string}>>({});



  // Add after other state declarations
  const [plotAllTogether, setPlotAllTogether] = useState(false);
  const [allChartZoom, setAllChartZoom] = useState(24);
  const [allChartTimeWindow, setAllChartTimeWindow] = useState<{start: dayjs.Dayjs, end: dayjs.Dayjs}>({
    start: dayjs().subtract(24, 'hour'),
    end: dayjs()
  });
  const [allChartDrag, setAllChartDrag] = useState<{ start: number | null, end: number | null, dragging: boolean }>({ start: null, end: null, dragging: false });

  // Add after other state declarations
  const [visibleChannels, setVisibleChannels] = useState<string[]>(selectedChannels);
  const [highlightedChannel, setHighlightedChannel] = useState<string | null>(null);
  const [showYAxis, setShowYAxis] = useState<Record<string, boolean>>({});

  // Add at the top of the component:
  const [chartViewMode, setChartViewMode] = useState<'individual' | 'combined'>('individual');

  // Add state for collapsible data table
  const [dataTableExpanded, setDataTableExpanded] = useState(false);

  // Add state for expanded individual graphs in Univariate mode
  const [expandedUnivariateGraphs, setExpandedUnivariateGraphs] = useState<Record<string, boolean>>({});

  // 1. Add hoveredPoint state at the top of the Dashboard component
  const [hoveredPoint, setHoveredPoint] = useState<Record<string, { x: string, y: number } | null>>({});

  // Fetch available channels from API
  const fetchAvailableChannels = async () => {
    try {
      setChannelsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/available-channels?prefix=MAIN.Dicing&hours=24`);
      const data = await response.json();
      
      if (data.error) {
        console.error('Error fetching available channels:', data.error);
        setAvailableChannels(DEFAULT_CHANNELS);
      } else {
        setAvailableChannels(data.channels || DEFAULT_CHANNELS);
        console.log(`Loaded ${data.channels?.length || 0} available channels`);
      }
    } catch (error) {
      console.error('Error fetching available channels:', error);
      setAvailableChannels(DEFAULT_CHANNELS);
    } finally {
      setChannelsLoading(false);
    }
  };

  // Load available channels on component mount
  useEffect(() => {
    fetchAvailableChannels();
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }
    };
  }, [refreshTimer]);

  // Fetch channel data without point limits - get all data for the time window
  const fetchChannelData = async (startTime?: dayjs.Dayjs, endTime?: dayjs.Dayjs, targetZoomLevel?: number, specificChannels?: string[], mode: 'aggregated' | 'raw' = 'aggregated', isLiveUpdate: boolean = false) => {
    if (!isLiveUpdate) setLoading(true);
    const channelsToFetch = specificChannels || selectedChannels;
    if (channelsToFetch.length === 0) return;
    
    let start: dayjs.Dayjs, end: dayjs.Dayjs;
    if (isLiveMode && isLiveUpdate) {
      // For live mode updates, use incremental fetching based on last fetched timestamp
      end = dayjs();
      const lastTimestamp = lastFetchedTimestamps[channelsToFetch[0]]; // Use first channel as reference
      if (lastTimestamp) {
        // Use precise timestamp for incremental fetch
        start = dayjs(lastTimestamp);
      } else {
        // First live update, fetch last 5 minutes
        start = dayjs().subtract(5, 'minute');
      }
    } else {
      // For initial loads and manual fetches, use the provided time window or current time window
      start = startTime || currentTimeWindow.start;
      end = endTime || currentTimeWindow.end;
    }
    
    const zoom = targetZoomLevel || zoomLevel;
    // Fetch all points for the interval without any limit

    const newLoadingMap: Record<string, boolean> = {};
    channelsToFetch.forEach(ch => newLoadingMap[ch] = true);
    setChannelLoadingMap(prev => ({ ...prev, ...newLoadingMap }));

    try {
      const fetchPromises = channelsToFetch.map(async (channel) => {
        try {
          // Convert dayjs objects to time expressions
          let startExpr: string, endExpr: string;
          
          if (isLiveMode && isLiveUpdate && start.isAfter(dayjs().subtract(1, 'hour'))) {
            // For live mode with recent timestamps, use precise ISO timestamps for incremental fetching
            startExpr = `"${start.toISOString()}"`;
            endExpr = `"${end.toISOString()}"`;
          } else {
            // For normal mode or older timestamps, use relative time expressions
            const now = dayjs();
            const startDiff = now.diff(start, 'minute');
            const endDiff = now.diff(end, 'minute');
            
            if (startDiff <= 0) {
              startExpr = 'now';
            } else if (startDiff < 60) {
              startExpr = `now-${startDiff}m`;
            } else if (startDiff < 1440) {
              startExpr = `now-${Math.round(startDiff / 60)}h`;
            } else {
              startExpr = `now-${Math.round(startDiff / 1440)}d`;
            }
            
            if (endDiff <= 0) {
              endExpr = 'now';
            } else if (endDiff < 60) {
              endExpr = `now-${endDiff}m`;
            } else if (endDiff < 1440) {
              endExpr = `now-${Math.round(endDiff / 60)}h`;
            } else {
              endExpr = `now-${Math.round(endDiff / 1440)}d`;
            }
          }
          
          const res = await fetch(`${API_BASE_URL}/api/channels-data`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              channels: [channel],
              start: startExpr,
              end: endExpr,
              // No limit
              mode: mode // 'aggregated' for chart, 'raw' for table
            })
          });
          const json = await res.json();
          if (json.error) {
            console.error(`Error fetching ${channel}:`, json.error);
            return { channel, data: [], count: 0 };
          }
          // Parse CSV data
          const rows = json.data.split('\n').filter((row: string) => row.trim() && !row.startsWith('#'));
          if (rows.length > 1) {
            const headers = rows[0].split(',');
            const data = rows.slice(1).map((row: string) => {
              const values = row.split(',');
              const obj: any = {};
              headers.forEach((header: string, index: number) => {
                obj[header.trim()] = values[index]?.trim();
              });
              return obj;
            });
            return { channel, data, count: json.dataPointCount || 0 };
          }
          return { channel, data: [], count: 0 };
        } catch (error) {
          console.error(`Error fetching ${channel}:`, error);
          return { channel, data: [], count: 0 };
        }
      });
      const results = await Promise.all(fetchPromises);
      const newDataMap: Record<string, any[]> = {};
      const newCountMap: Record<string, number> = {};
      const newLoadingMap: Record<string, boolean> = {};
      
      results.forEach(({ channel, data, count }) => {
        if (isLiveMode && isLiveUpdate) {
          // For live mode updates, append new data to existing data and track latest timestamp
          const existingData = channelDataMap[channel] || [];
          if (data.length > 0) {
            // Append new data (no need to deduplicate since we're using incremental fetching)
            const merged = [...existingData, ...data];
            
            // Trim to maintain a reasonable data size (last 5 minutes)
            const fiveMinutesAgo = dayjs().subtract(5, 'minute');
            const trimmed = merged.filter(row => dayjs(row._time).isAfter(fiveMinutesAgo));
            
            newDataMap[channel] = trimmed;
            newCountMap[channel] = count;
            
            // Track the latest timestamp for this channel
            const latestTimestamp = data[data.length - 1]?._time;
            if (latestTimestamp) {
              setLastFetchedTimestamps(prev => ({ ...prev, [channel]: latestTimestamp }));
            }
          } else {
            // No new data, keep existing data
            newDataMap[channel] = existingData;
            newCountMap[channel] = channelDataPointCounts[channel] || 0;
          }
        } else {
          // For initial loads and manual fetches, replace data and reset last fetched timestamp
          newDataMap[channel] = data;
          newCountMap[channel] = count;
          
          // Reset last fetched timestamp for non-live mode
          if (data.length > 0) {
            const latestTimestamp = data[data.length - 1]?._time;
            if (latestTimestamp) {
              setLastFetchedTimestamps(prev => ({ ...prev, [channel]: latestTimestamp }));
            }
          }
        }
        newLoadingMap[channel] = false;
      });
      
      setChannelDataMap(prev => ({ ...prev, ...newDataMap }));
      setChannelDataPointCounts(prev => ({ ...prev, ...newCountMap }));
      setChannelLoadingMap(prev => ({ ...prev, ...newLoadingMap }));
      
      if (startTime && endTime && !isLiveUpdate) {
        setCurrentTimeWindow({ start, end });
        setTimeRange({ 
          start: start.toISOString(), 
          end: end.toISOString() 
        });
      }
      // In live mode, DO NOT update the currentTimeWindow - keep it static
      // Only update the chart data, not the time window
      if (isLiveMode && isLiveUpdate) {
        // Keep the time window static - don't update it
        // The charts will show the sliding window of data within the static time window
        console.log('Live mode update - keeping time window static');
      }
    } catch (error) {
      console.error('Error fetching channel data:', error);
    } finally {
      if (!isLiveUpdate) setLoading(false);
    }
  };

  // Set initial time window on mount and fetch data
  useEffect(() => {
    // Use last 24 hours as default
    const defaultStart = dayjs().subtract(24, 'hour');
    const defaultEnd = dayjs();
    setTimeRange({ start: defaultStart.toISOString(), end: defaultEnd.toISOString() });
    setFrom(defaultStart.toISOString());
    setTo(defaultEnd.toISOString());
    setQuickRangeLabel('Last 24 hours');
    setCurrentTimeWindow({ start: defaultStart, end: defaultEnd });
    if (tableChannel === '' && selectedChannels.length > 0) setTableChannel(selectedChannels[0]);
  }, []); // Only run on mount, not when selectedChannels changes

  // Handle selectedChannels changes and fetch initial data
  useEffect(() => {
    if (selectedChannels.length > 0) {
      if (tableChannel === '') {
        setTableChannel(selectedChannels[0]);
      }
      // Fetch initial data when channels are selected
      console.log('Fetching initial data for selected channels');
      fetchChannelData(currentTimeWindow.start, currentTimeWindow.end);
    }
  }, [selectedChannels]);

  // Fetch initial data using relative time expressions
  const fetchInitialData = async () => {
    if (selectedChannels.length === 0) return;
    
    console.log('Fetching initial 24-hour dataset for:', selectedChannels);
    
    setLoading(true);
    
    // Set loading state for each channel
    const newLoadingMap: Record<string, boolean> = {};
    selectedChannels.forEach(ch => newLoadingMap[ch] = true);
    setChannelLoadingMap(newLoadingMap);
    
    try {
      // Fetch each channel individually for full 24 hours using relative time
      const fetchPromises = selectedChannels.map(async (channel) => {
        try {
          const res = await fetch(`${API_BASE_URL}/api/channels-data`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              channels: [channel],
              start: 'now-24h',
              end: 'now',
              mode: 'raw'
            })
          });
          
          const json = await res.json();
          if (json.error) {
            console.error(`Error fetching ${channel}:`, json.error);
            return { channel, data: [], count: 0 };
          }
          
          console.log(`Initial fetch ${channel}: received ${json.dataPointCount || 0} points, actual data length: ${json.data.split('\n').filter((row: string) => row.trim() && !row.startsWith('#')).length - 1}`);
          
          // Parse CSV data
          const rows = json.data.split('\n').filter((row: string) => row.trim() && !row.startsWith('#'));
          if (rows.length > 1) {
            const headers = rows[0].split(',');
            const data = rows.slice(1).map((row: string) => {
              const values = row.split(',');
              const obj: any = {};
              headers.forEach((header: string, index: number) => {
                obj[header.trim()] = values[index]?.trim();
              });
              return obj;
            });
            return { channel, data, count: json.dataPointCount || 0 };
          }
          return { channel, data: [], count: 0 };
        } catch (error) {
          console.error(`Error fetching ${channel}:`, error);
          return { channel, data: [], count: 0 };
        }
      });
      
      // Wait for all channels to complete
      const results = await Promise.all(fetchPromises);
      
      console.log('Initial data results:', results.map(r => ({
        channel: r.channel,
        dataPoints: r.data.length,
        count: r.count
      })));
      
      // Update display data
      const newDataMap: Record<string, any[]> = {};
      const newCountMap: Record<string, number> = {};
      const newLoadingMap: Record<string, boolean> = {};
      
      results.forEach(({ channel, data, count }) => {
        newDataMap[channel] = data;
        newCountMap[channel] = count;
        newLoadingMap[channel] = false;
      });
      
      setChannelDataMap(newDataMap);
      setChannelDataPointCounts(newCountMap);
      setChannelLoadingMap(newLoadingMap);
      
      // Initialize channel zoom levels and time windows
      const newChannelZoomLevels: Record<string, number> = {};
      const newChannelTimeWindows: Record<string, {start: dayjs.Dayjs, end: dayjs.Dayjs}> = {};
      
      results.forEach(({ channel }) => {
        newChannelZoomLevels[channel] = 24; // Default to 24-hour zoom
      });
      
      setChannelZoomLevels(newChannelZoomLevels);
      
      // Set individual channel time windows to the full 24-hour range
      const defaultStart = dayjs().subtract(24, 'hour');
      const defaultEnd = dayjs();
      
      results.forEach(({ channel }) => {
        newChannelTimeWindows[channel] = { start: defaultStart, end: defaultEnd };
      });
      setChannelTimeWindows(newChannelTimeWindows);
      
      console.log('Set channel time windows to 24-hour range:', defaultStart.format('YYYY-MM-DD HH:mm:ss'), 'to', defaultEnd.format('YYYY-MM-DD HH:mm:ss'));
      
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Time range quick picker
  const handleQuickRange = (range: typeof QUICK_RANGES[0]) => {
    if (!range.value) return; // Prevents error for 'Custom'
    const start = range.value.start();
    const end = range.value.end();
    setTimeRange({ start: start.toISOString(), end: end.toISOString() });
    setCustomRange(null);
    setFrom(start.toISOString());
    setTo(end.toISOString());
    setQuickRangeLabel(range.label);
    fetchChannelData(start, end, zoomLevel);
  };

  // Custom range apply
  const handleApplyCustomRange = () => {
    if (customRange) {
      setTimeRange(customRange);
      // Fetch new data for the custom time range
      fetchChannelData(dayjs(customRange.start), dayjs(customRange.end), zoomLevel);
    }
  };

  // Time navigation functions
  const navigateTime = (direction: 'forward' | 'backward', steps: number = 1) => {
    const duration = direction === 'forward' ? 1 : -1;
    
    // Calculate the time interval based on current zoom level
    let timeInterval: dayjs.ManipulateType;
    let intervalSteps: number;
    
    if (zoomLevel >= 24) {
      timeInterval = 'hour';
      intervalSteps = 1;
    } else if (zoomLevel >= 1) {
      timeInterval = 'hour';
      intervalSteps = 24 / zoomLevel;
    } else {
      timeInterval = 'minute';
      intervalSteps = Math.abs(zoomLevel) * 60;
    }
    
    const newStart = currentTimeWindow.start.add(steps * duration * intervalSteps, timeInterval);
    const newEnd = currentTimeWindow.end.add(steps * duration * intervalSteps, timeInterval);
    
    console.log(`Navigating ${direction} by ${steps} ${timeInterval}(s):`, {
      from: newStart.format('YYYY-MM-DD HH:mm:ss'),
      to: newEnd.format('YYYY-MM-DD HH:mm:ss'),
      zoomLevel,
      timeInterval,
      intervalSteps
    });
    
    // Fetch new data for the new time window
    fetchChannelData(newStart, newEnd, zoomLevel);
  };

  const zoomIn = () => {
    // Zoom levels: 1s -> 5s -> 15s -> 30s -> 1m -> 5m -> 15m -> 30m -> 1h -> 3h -> 6h -> 12h -> 24h -> 48h -> 72h -> 168h -> 336h -> 720h
    const zoomLevels = [1/3600, 1/720, 0.25/60, 0.5/60, 1/60, 1/12, 0.25, 0.5, 1, 3, 6, 12, 24, 48, 72, 168, 336, 720];
    const currentIndex = zoomLevels.findIndex(level => Math.abs(level - zoomLevel) < 0.01);
    const newIndex = Math.min(zoomLevels.length - 1, currentIndex + 1);
    const newZoomLevel = zoomLevels[newIndex];
    
    setZoomLevel(newZoomLevel);
    updateTimeWindowForZoom(newZoomLevel);
  };

  const zoomOut = () => {
    // Zoom levels: 1s -> 5s -> 15s -> 30s -> 1m -> 5m -> 15m -> 30m -> 1h -> 3h -> 6h -> 12h -> 24h -> 48h -> 72h -> 168h -> 336h -> 720h
    const zoomLevels = [1/3600, 1/720, 0.25/60, 0.5/60, 1/60, 1/12, 0.25, 0.5, 1, 3, 6, 12, 24, 48, 72, 168, 336, 720];
    const currentIndex = zoomLevels.findIndex(level => Math.abs(level - zoomLevel) < 0.01);
    const newIndex = Math.max(0, currentIndex - 1);
    const newZoomLevel = zoomLevels[newIndex];
    
    setZoomLevel(newZoomLevel);
    updateTimeWindowForZoom(newZoomLevel);
  };

  const updateTimeWindowForZoom = (newZoomLevel: number) => {
    // Calculate the center of the current time window
    const center = currentTimeWindow.start.add(currentTimeWindow.end.diff(currentTimeWindow.start) / 2);
    
    // Calculate the new duration based on zoom level (in hours)
    const newDurationHours = newZoomLevel;
    const newDurationMs = newDurationHours * 60 * 60 * 1000; // Convert hours to milliseconds
    const halfDuration = newDurationMs / 2;
    
    // Create new time window centered on the same point
    const newStart = center.subtract(halfDuration, 'millisecond');
    const newEnd = center.add(halfDuration, 'millisecond');
    
    // Update the current time window
    setCurrentTimeWindow({ start: newStart, end: newEnd });
    
    // Fetch data for the new time window
    fetchChannelData(newStart, newEnd, undefined, undefined, 'aggregated');
  };

  // Individual channel zoom functions
  const zoomInChannel = (channel: string) => {
    const currentZoom = channelZoomLevels[channel] || 24;
    const zoomLevels = [1/3600, 1/720, 0.25/60, 0.5/60, 1/60, 1/12, 0.25, 0.5, 1, 3, 6, 12, 24, 48, 72, 168, 336, 720];
    const currentIndex = zoomLevels.findIndex(level => Math.abs(level - currentZoom) < 0.01);
    const newIndex = Math.min(zoomLevels.length - 1, currentIndex + 1);
    const newZoomLevel = zoomLevels[newIndex];
    
    setChannelZoomLevels(prev => ({ ...prev, [channel]: newZoomLevel }));
    updateChannelTimeWindow(channel, newZoomLevel);
  };

  const zoomOutChannel = (channel: string) => {
    const currentZoom = channelZoomLevels[channel] || 24;
    const zoomLevels = [1/3600, 1/720, 0.25/60, 0.5/60, 1/60, 1/12, 0.25, 0.5, 1, 3, 6, 12, 24, 48, 72, 168, 336, 720];
    const currentIndex = zoomLevels.findIndex(level => Math.abs(level - currentZoom) < 0.01);
    const newIndex = Math.max(0, currentIndex - 1);
    const newZoomLevel = zoomLevels[newIndex];
    
    setChannelZoomLevels(prev => ({ ...prev, [channel]: newZoomLevel }));
    updateChannelTimeWindow(channel, newZoomLevel);
  };

  const updateChannelTimeWindow = (channel: string, newZoomLevel: number) => {
    const currentWindow = channelTimeWindows[channel] || currentTimeWindow;
    
    // Calculate the center of the current time window
    const center = currentWindow.start.add(currentWindow.end.diff(currentWindow.start) / 2);
    
    // Calculate the new duration based on zoom level (in hours)
    const newDurationHours = newZoomLevel;
    const newDurationMs = newDurationHours * 60 * 60 * 1000; // Convert hours to milliseconds
    const halfDuration = newDurationMs / 2;
    
    // Create new time window centered on the same point
    const newStart = center.subtract(halfDuration, 'millisecond');
    const newEnd = center.add(halfDuration, 'millisecond');
    
    setChannelTimeWindows(prev => ({ ...prev, [channel]: { start: newStart, end: newEnd } }));
    fetchChannelData(newStart, newEnd, undefined, [channel], 'aggregated');
  };

  const navigateChannelTime = (channel: string, direction: 'forward' | 'backward', steps: number = 1) => {
    const currentWindow = channelTimeWindows[channel] || currentTimeWindow;
    const currentZoom = channelZoomLevels[channel] || 24;
    const duration = direction === 'forward' ? 1 : -1;
    
    // Calculate the time interval based on current zoom level
    let timeInterval: dayjs.ManipulateType;
    let intervalSteps: number;
    
    if (currentZoom >= 24) {
      timeInterval = 'hour';
      intervalSteps = 1;
    } else if (currentZoom >= 1) {
      timeInterval = 'hour';
      intervalSteps = 24 / currentZoom;
    } else {
      timeInterval = 'minute';
      intervalSteps = Math.abs(currentZoom) * 60;
    }
    
    const newStart = currentWindow.start.add(steps * duration * intervalSteps, timeInterval);
    const newEnd = currentWindow.end.add(steps * duration * intervalSteps, timeInterval);
    
    setChannelTimeWindows(prev => ({ ...prev, [channel]: { start: newStart, end: newEnd } }));
    fetchChannelData(newStart, newEnd, undefined, [channel], 'aggregated');
  };

  const resetChannelView = (channel: string) => {
    const fullStart = dayjs().subtract(24, 'hour');
    const fullEnd = dayjs();
    setChannelZoomLevels(prev => ({ ...prev, [channel]: 24 }));
    setChannelTimeWindows(prev => ({ ...prev, [channel]: { start: fullStart, end: fullEnd } }));
    fetchChannelData(fullStart, fullEnd, 24, [channel], 'aggregated');
  };

  const toggleCardExpansion = (channel: string) => {
    setExpandedCards(prev => ({
      ...prev,
      [channel]: !prev[channel]
    }));
  };

  // Auto-refresh management
  const startAutoRefresh = (interval: number) => {
    // Clear any existing timer
    if (refreshTimer) {
      clearInterval(refreshTimer);
    }
    
    if (interval > 0) {
      const isAuto = interval === 1000; // 1 second
      setIsLiveMode(isAuto);
      
      const timer = setInterval(() => {
        if (isAuto) {
          // Auto mode: fetch live data every second
          fetchChannelData(undefined, undefined, undefined, undefined, 'aggregated', true);
        } else {
          // Other intervals: refresh current view
          fetchChannelData();
        }
      }, interval);
      
      setRefreshTimer(timer);
    } else {
      setIsLiveMode(false);
      setRefreshTimer(null);
    }
  };

  const stopAutoRefresh = () => {
    if (refreshTimer) {
      clearInterval(refreshTimer);
      setRefreshTimer(null);
    }
    if (paintTimer) {
      clearInterval(paintTimer);
      setPaintTimer(null);
    }
    setIsLiveMode(false);
    // Reset last fetched timestamps when stopping live mode
    setLastFetchedTimestamps({});
  };

  // Handle refresh interval change


  const KpiCard = ({ title, value, unit, color, trend }: { 
    title: string; 
    value: number; 
    unit: string; 
    color: string; 
    trend?: string; 
  }) => (
    <Card sx={{ height: '100%', borderRadius: 3 }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h4" component="div" sx={{ color, fontWeight: 700 }}>
          {value}{unit}
        </Typography>
        {trend && (
          <Typography variant="body2" color="text.secondary">
            {trend}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  // For all top section dropdowns (Quick Range, From, To), use the same size and style
  const topDropdownSx = {
    width: 200,
    height: 44,
    borderRadius: 24,
    bgcolor: '#fff',
    border: '1.5px solid #00adef',
    fontWeight: 700,
    fontSize: '1.1rem',
    px: 2,
    py: 1,
  };

  // Flip button hover styles for outlined buttons in the top section
  const topButtonSx = {
    height: 40,
    px: 3,
    fontSize: '0.875rem',
    fontWeight: 600,
    bgcolor: 'primary.main',
    color: '#fff',
    borderColor: 'primary.main',
    boxShadow: 2,
    '&:hover': {
      bgcolor: '#fff',
      color: 'primary.main',
      borderColor: 'primary.main',
      boxShadow: 3,
    },
  };

  // Bulletproof guard for DatePicker values
  const getValidDayjsOrNull = (value: any): dayjs.Dayjs | null => {
    if (dayjs.isDayjs(value) && value.isValid()) return value;
    const d = dayjs(value);
    return d.isValid() ? d : null;
  };

  // Defensive setCustomDateRange wrapper with explicit types and bulletproof coercion
  const safeSetCustomDateRange = (
    updater: ((prev: [string, string]) => [string, string]) | [string, string]
  ) => {
    setCustomDateRange((prev: [string, string]) => {
      let next = typeof updater === 'function' ? (updater as (prev: [string, string]) => [string, string])(prev) : updater;
      return next;
    });
  };

  // Use safeSetCustomDateRange everywhere instead of setCustomDateRange
  const handleOpenCustomRange = () => {
    setCustomRangeDialogOpen(true);
    safeSetCustomDateRange(prev => {
      const today = dayjs().format('YYYY-MM-DD');
      const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
      const start = prev[0] && dayjs(prev[0], 'YYYY-MM-DD', true).isValid() ? prev[0] : yesterday;
      const end = prev[1] && dayjs(prev[1], 'YYYY-MM-DD', true).isValid() ? prev[1] : today;
      return [start, end];
    });
  };

  // --- 1. Fix getTickFormat and getTickFormatterWithDayTransitions ---
  const getTickFormat = React.useCallback((start: dayjs.Dayjs, end: dayjs.Dayjs, zoomLevel?: number) => {
    const diffMs = end.diff(start, 'millisecond');
    if (zoomLevel !== undefined) {
      if (zoomLevel <= 1/3600) return 'ss.SSS'; // 1 second or less
      if (zoomLevel <= 1/60) return 'HH:mm:ss'; // 1 minute or less
      if (zoomLevel <= 1/12) return 'HH:mm:ss'; // 5 minutes or less
      if (zoomLevel <= 1) return 'HH:mm:ss'; // 1 hour or less
      if (zoomLevel <= 24) return 'HH:mm:ss'; // 1 day or less - always show time only
      return 'MMM D'; // More than 1 day
    }
    if (diffMs < 24 * 60 * 60 * 1000) return 'HH:mm:ss'; // < 24 hours - show seconds
    return 'YYYY-MM-DD';
  }, []);

  const getTickFormatterWithDayTransitions = React.useCallback((
    ticks: string[],
    start: dayjs.Dayjs,
    end: dayjs.Dayjs,
    zoomLevel?: number
  ): ((value: string, index: number) => string) => {
    return (value: string, index: number) => {
      const thisDate = dayjs(value);
      if (zoomLevel !== undefined) {
        if (zoomLevel <= 1/3600) return thisDate.format('ss.SSS');
        if (zoomLevel <= 1/60) return thisDate.format('HH:mm:ss');
        if (zoomLevel <= 1/12) return thisDate.format('HH:mm:ss');
        if (zoomLevel <= 1) return thisDate.format('HH:mm:ss');
        if (zoomLevel <= 24) return thisDate.format('HH:mm:ss'); // always time only
        return thisDate.format('MMM D');
      }
      const diffMs = end.diff(start, 'millisecond');
      if (diffMs < 24 * 60 * 60 * 1000) return thisDate.format('HH:mm:ss');
      return thisDate.format('YYYY-MM-DD');
    };
  }, []);

  // --- 2. Drag selection already only updates the selected channel's time window (confirmed in setChannelTimeWindows) ---
  // --- 3. Fix hover logic for vertical ReferenceLine ---


  // Update visibleChannels when selectedChannels changes
  useEffect(() => {
    setVisibleChannels(selectedChannels);
  }, [selectedChannels]);

  // Inside the Dashboard component, before the return statement:
  const channelsWithData = React.useMemo(() => visibleChannels.filter((ch: string) => {
    const data = (channelDataMap[ch] || []).filter((row: any) => {
      const t = dayjs(row._time);
      return t.isAfter(allChartTimeWindow.start) && t.isBefore(allChartTimeWindow.end);
    });
    return data.length > 0;
  }), [visibleChannels, channelDataMap, allChartTimeWindow.start.valueOf(), allChartTimeWindow.end.valueOf(), lastPaintTime]);



  

  // Auto-refresh timer - updates graph cards with current time window
  useEffect(() => {
    if (isLiveMode && selectedChannels.length > 0) {
      console.log('Starting auto-refresh timer');
      
      // Data fetch timer - every 1 second
      const fetchInterval = setInterval(() => {
        console.log('Auto-refresh: fetching data at', dayjs().format('HH:mm:ss'));
        
        // Advance the time window to show the most recent data
        const end = dayjs();
        const duration = currentTimeWindow.end.diff(currentTimeWindow.start);
        const start = end.subtract(duration, 'millisecond');
        
        console.log('Auto-refresh: time window', start.format('HH:mm:ss'), 'to', end.format('HH:mm:ss'));
        
        // Update the current time window to advance with time
        setCurrentTimeWindow({ start, end });
        
        // Update individual channel time windows to advance with time
        const newChannelTimeWindows: Record<string, {start: dayjs.Dayjs, end: dayjs.Dayjs}> = {};
        selectedChannels.forEach(ch => {
          const currentChannelWindow = channelTimeWindows[ch] || currentTimeWindow;
          const channelDuration = currentChannelWindow.end.diff(currentChannelWindow.start);
          const channelStart = end.subtract(channelDuration, 'millisecond');
          newChannelTimeWindows[ch] = { start: channelStart, end };
        });
        setChannelTimeWindows(prev => ({ ...prev, ...newChannelTimeWindows }));
        
        // Fetch data for the new time window
        fetchChannelData(start, end, undefined, undefined, 'aggregated', true);
      }, 1000);
      
      // Screen update timer - every 2 seconds
      const paintInterval = setInterval(() => {
        console.log('Auto-refresh: updating screen at', dayjs().format('HH:mm:ss'));
        // Force a re-render by updating a state that triggers chart updates
        setLastPaintTime(dayjs().valueOf());
      }, 2000);
      
      setRefreshTimer(fetchInterval);
      setPaintTimer(paintInterval);
      
      return () => {
        console.log('Clearing auto-refresh timers');
        clearInterval(fetchInterval);
        clearInterval(paintInterval);
      };
    } else {
      // Clear timers when not in live mode
      if (refreshTimer) {
        clearInterval(refreshTimer);
        setRefreshTimer(null);
      }
      if (paintTimer) {
        clearInterval(paintTimer);
        setPaintTimer(null);
      }
    }
  }, [isLiveMode, selectedChannels.length, currentTimeWindow.start, currentTimeWindow.end]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <Box
        sx={theme => ({
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          p: 2,
          borderRadius: 4,
          bgcolor: theme.palette.mode === 'dark' ? '#15171c' : '#fff',
          minHeight: 110,
          gap: 3,
        })}
      >
        {/* Tool Thumbnail and Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 72, height: 72, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src="/tool-thumbnail.png" alt="Tool Thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main', mb: 0 }}>
              {(() => {
                let suffix = '01';
                if (/2|02/.test(selectedTool)) suffix = '02';
                return `Plasma Dicing ${suffix} Dashboard`;
              })()}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {availableChannels.length} channels available
            </Typography>
          </Box>
        </Box>
        {/* Samba Logo */}
        <img src="/samba-logo.png" alt="Samba Logo" style={{ height: 48, marginRight: 8 }} />
      </Box>

      {/* Controls Row - previous styling restored */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
        {/* Channel selector */}
        <Autocomplete
          multiple
          options={availableChannels}
          value={selectedChannels}
          onChange={(_, value) => {
            setSelectedChannels(value);
            // Ensure new channels are expanded by default
            setExpandedCards(prev => {
              const updated = { ...prev };
              value.forEach(ch => {
                if (!(ch in updated)) {
                  updated[ch] = true; // Expand new channels by default
                }
              });
              return updated;
            });
          }}
          loading={channelsLoading}
          disableCloseOnSelect
          renderInput={params => (
            <TextField
              {...params}
              label={`Available Channels (${selectedChannels.length} selected)`}
              variant="outlined"
              size="small"
              sx={{ minWidth: 400 }}
            />
          )}
          renderTags={() => null} // Hide chips
          sx={{ minWidth: 400 }}
        />
        {/* Quick Range */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Quick Range</InputLabel>
          <Select
            value={quickRangeLabel}
            label="Quick Range"
            onChange={e => {
              const range = QUICK_RANGES.find(r => r.label === e.target.value);
              if (range) handleQuickRange(range);
            }}
          >
            {QUICK_RANGES.map(r => (
              <MenuItem key={r.label} value={r.label}>{r.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Refresh Channels */}
        <Button variant="contained" color="primary" onClick={fetchAvailableChannels} sx={{ ml: 2 }}>Refresh Channels</Button>
        {/* Fetch Data */}
        <Button 
          variant="outlined" 
          onClick={() => {
            if (selectedChannels.length > 0 && !isLiveMode) {
              console.log('Manual fetch triggered');
              fetchChannelData(currentTimeWindow.start, currentTimeWindow.end);
            }
          }}
          disabled={selectedChannels.length === 0 || isLiveMode}
          sx={{ ml: 2 }}
        >
          Fetch Data
        </Button>
        {/* Auto Refresh */}
        <Button 
          variant="contained" 
          color={isLiveMode ? "error" : "primary"} 
          onClick={() => {
            if (isLiveMode) {
              console.log('Stopping auto-refresh');
              stopAutoRefresh();
              // Restore the original time window when stopping auto-refresh
              const originalStart = dayjs(from);
              const originalEnd = dayjs(to);
              setCurrentTimeWindow({ start: originalStart, end: originalEnd });
            } else {
              console.log('Starting auto-refresh');
              setIsLiveMode(true);
            }
          }}
          disabled={selectedChannels.length === 0}
          sx={{ ml: 2 }}
        >
          {isLiveMode ? "Stop Auto-Refresh" : "Start Auto-Refresh"}
        </Button>
      </Box>

      {/* Time Selection Controls - always visible */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        {/* Quick range dropdown, custom range, etc. (reuse your existing controls here) */}
        {/* ...existing time selection controls... */}
      </Box>

      {/* Data Table */}
      <Paper sx={{ p: 3, borderRadius: 3, mt: 3 }}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Raw Channel Data
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            ({channelDataPointCounts[tableChannel] || 0} data points from server)
          </Typography>
          <FormControl sx={{ 
            minWidth: 280,
            '& .MuiInputLabel-root': {
              fontSize: '0.875rem'
            },
            '& .MuiSelect-select': {
              fontSize: '0.875rem',
              py: 1.5
            }
          }}>
            <InputLabel id="table-channel-label">Channel</InputLabel>
            <Select
              labelId="table-channel-label"
              value={tableChannel}
              onChange={e => setTableChannel(e.target.value)}
              input={<OutlinedInput label="Channel" />}
              size="small"
            >
              {selectedChannels.map(ch => (
                <MenuItem key={ch} value={ch}>{ch}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton 
            onClick={() => setDataTableExpanded(!dataTableExpanded)}
            size="small"
            sx={{ ml: 'auto' }}
          >
            {dataTableExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        {dataTableExpanded && (
          <TableContainer component={Paper} sx={{ maxHeight: 350 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(channelDataMap[tableChannel] || []).slice(-25).map((row, i) => (
                  <TableRow key={tableChannel + i}>
                    <TableCell>{row._time}</TableCell>
                    <TableCell>{row._measurement}</TableCell>
                    <TableCell>{row._value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Loading Spinner */}
      {loading && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          bgcolor: 'rgba(255,255,255,0.6)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <CircularProgress size={64} color="primary" />
        </Box>
      )}

      {/* Custom Range Picker Dialog */}
      <Dialog open={customRangeDialogOpen} onClose={() => setCustomRangeDialogOpen(false)}>
        <DialogTitle>Select Custom Date Range</DialogTitle>
        <DialogContent sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
          <input
            type="date"
            value={customDateRange[0]}
            max={customDateRange[1]}
            onChange={e => setCustomDateRange([e.target.value, customDateRange[1]])}
            style={{ fontSize: '1rem', padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc' }}
          />
          <Box sx={{ mx: 1 }}> to </Box>
          <input
            type="date"
            value={customDateRange[1]}
            min={customDateRange[0]}
            max={dayjs().format('YYYY-MM-DD')}
            onChange={e => setCustomDateRange([customDateRange[0], e.target.value])}
            style={{ fontSize: '1rem', padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomRangeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setCustomRangeDialogOpen(false);
              const start = dayjs(customDateRange[0], 'YYYY-MM-DD', true);
              const end = dayjs(customDateRange[1], 'YYYY-MM-DD', true);
              if (start.isValid() && end.isValid()) {
                setTimeRange({ start: start.toISOString(), end: end.toISOString() });
                setQuickRangeLabel('Custom');
                setCurrentTimeWindow({ start, end });
                fetchChannelData(start, end, zoomLevel);
              }
            }}
            disabled={
              !customDateRange[0] || !customDateRange[1] ||
              !dayjs(customDateRange[0], 'YYYY-MM-DD', true).isValid() ||
              !dayjs(customDateRange[1], 'YYYY-MM-DD', true).isValid()
            }
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Channel Selection Dialog */}
      <Dialog open={channelDialogOpen} onClose={() => setChannelDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Select Channels</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pt: 2, pb: 1 }}>
            <SearchIcon sx={{ color: '#888', mr: 1 }} />
            <TextField
              placeholder="Search channels..."
              value={channelSearch}
              onChange={e => setChannelSearch(e.target.value)}
              size="small"
              fullWidth
              variant="standard"
              InputProps={{ disableUnderline: true }}
            />
          </Box>
          <Box sx={{ maxHeight: 320, overflowY: 'auto', px: 2, pb: 2 }}>
            {(() => {
              const filtered = availableChannels.filter(ch => ch.toLowerCase().includes(channelSearch.toLowerCase()));
              const selected = filtered.filter(ch => selectedChannels.includes(ch));
              const unselected = filtered.filter(ch => !selectedChannels.includes(ch));
              return [
                ...selected.map(ch => (
                  <Box key={ch} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                    <Checkbox
                      checked
                      onChange={e => {
                        setSelectedChannels(selectedChannels.filter(sel => sel !== ch));
                      }}
                      color="primary"
                      sx={{ p: 0.5, mr: 1 }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#111' }}>{ch}</Typography>
                  </Box>
                )),
                ...unselected.map(ch => (
                  <Box key={ch} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                    <Checkbox
                      checked={false}
                      onChange={e => {
                        setSelectedChannels([...selectedChannels, ch]);
                        // Ensure new channels are expanded by default
                        setExpandedCards(prev => ({
                          ...prev,
                          [ch]: true
                        }));
                      }}
                      color="primary"
                      sx={{ p: 0.5, mr: 1 }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 400, color: '#111' }}>{ch}</Typography>
                  </Box>
                ))
              ];
            })()}
            {availableChannels.filter(ch => ch.toLowerCase().includes(channelSearch.toLowerCase())).length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No channels found
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChannelDialogOpen(false)} color="primary" variant="outlined">Cancel</Button>
          <Button onClick={() => setChannelDialogOpen(false)} color="primary" variant="contained">Apply</Button>
        </DialogActions>
      </Dialog>

      {/* Chart Rendering Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 4, gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Chart View</InputLabel>
          <Select
            value={chartViewMode}
            label="Chart View"
            onChange={e => setChartViewMode(e.target.value as 'individual' | 'combined')}
          >
            <MenuItem value="individual">Univariate</MenuItem>
            <MenuItem value="combined">Multivariate</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {chartViewMode === 'individual' && (
        <Box>
          {selectedChannels
            .filter(ch => {
              const data = channelDataMap[ch] || [];
              // Use individual channel time window for filtering, not the global currentTimeWindow
              const channelWindow = channelTimeWindows[ch] || currentTimeWindow;
              const values = data
                .filter(row => {
                  const t = dayjs(row._time);
                  return t.isAfter(channelWindow.start) && t.isBefore(channelWindow.end);
                })
                .map(row => parseFloat(row._value))
                .filter(v => !isNaN(v));
              return values.length > 0;
            })
            .map((ch, idx) => {
              const channelDataForChart = channelDataMap[ch] || [];
              let validData: ChartDataPoint[] = channelDataForChart.map((row, index) => {
                const time = dayjs(row._time);
                return {
                  _binned: time.toISOString(),
                  _value: parseFloat(row._value),
                  _time: row._time,
                  isRecent: isLiveMode && index >= channelDataForChart.length - 10
                };
              });
              // During auto-refresh, use currentTimeWindow for both display and filtering
              // Otherwise use individual channel time window
              const windowStart = isLiveMode ? currentTimeWindow.start : (channelTimeWindows[ch] || currentTimeWindow).start;
              const windowEnd = isLiveMode ? currentTimeWindow.end : (channelTimeWindows[ch] || currentTimeWindow).end;
              validData = validData.filter(d => {
                const t = dayjs(d._time);
                return t.isAfter(windowStart) && t.isBefore(windowEnd);
              });
              
              // --- Univariate chart dummy point logic ---
              if (isLiveMode) {
                const nowISO = dayjs().toISOString();
                if (!validData.some(d => d._time === nowISO)) {
                  validData = [
                    ...validData,
                    {
                      _binned: nowISO,
                      _value: null,
                      _time: nowISO,
                      isRecent: false,
                      _paintTime: lastPaintTime
                    } as ChartDataPoint
                  ];
                }
                validData = validData.map(d => ({ ...d, _paintTime: lastPaintTime }));
              }
              

              // Calculate X-axis ticks from filtered data
              const filteredTimestamps = validData.map(d => d._time).sort();
              let ticks = filteredTimestamps.length <= 10 ? filteredTimestamps : Array.from({ length: 10 }, (_, i) => {
                const idx = Math.round(i * (filteredTimestamps.length - 1) / 9);
                return filteredTimestamps[idx];
              });
              
              // In live mode, ensure the last tick shows the current time
              if (isLiveMode && ticks.length > 0) {
                const currentTime = dayjs().toISOString();
                // Replace the last tick with current time if it's more recent
                const lastTick = dayjs(ticks[ticks.length - 1]);
                const now = dayjs();
                if (now.isAfter(lastTick)) {
                  ticks[ticks.length - 1] = currentTime;
                }
              }
              const channelZoom = channelZoomLevels[ch] || 24;
              const tickFormat = getTickFormat(windowStart, windowEnd, channelZoom);
              return (
                <Box key={ch} sx={{ 
                  mb: 1, 
                  background: '#fff', 
                  borderRadius: 2, 
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)', 
                  border: '1px solid #e0e0e0', 
                  p: 1,
                  userSelect: (dragState[ch]?.dragging) ? 'none' : 'auto',
                  WebkitUserSelect: (dragState[ch]?.dragging) ? 'none' : 'auto',
                  MozUserSelect: (dragState[ch]?.dragging) ? 'none' : 'auto',
                  msUserSelect: (dragState[ch]?.dragging) ? 'none' : 'auto'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: getColorForChannel(ch, idx) }}>{ch}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Tooltip title="Zoom Out">
                        <IconButton size="small" onClick={() => zoomOutChannel(ch)}>
                          <ZoomOutIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Zoom In">
                        <IconButton size="small" onClick={() => zoomInChannel(ch)}>
                          <ZoomInIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset">
                        <IconButton size="small" onClick={() => resetChannelView(ch)}>
                          <ReplayIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={expandedUnivariateGraphs[ch] ? 'Collapse' : 'Expand'}>
                        <IconButton size="small" onClick={() => {
                          setExpandedUnivariateGraphs(prev => ({
                            ...prev,
                            [ch]: !prev[ch]
                          }));
                        }}>
                          {expandedUnivariateGraphs[ch] ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <ResponsiveContainer width="100%" height={expandedUnivariateGraphs[ch] ? 300 : 120}>
                    <LineChart
                      key={`${ch}-${lastPaintTime}`}
                      data={validData}
                      margin={{ top: 10, right: 20, left: expandedUnivariateGraphs[ch] ? 60 : 40, bottom: 20 }}
                      onMouseDown={(e) => {
                        if (e && e.activeLabel && validData.length > 0) {
                          // Find the closest data point to the clicked position
                          const clickedTime = dayjs(e.activeLabel);
                          let closestIdx = 0;
                          let minDiff = Math.abs(dayjs(validData[0]._time).diff(clickedTime));
                          
                          for (let i = 1; i < validData.length; i++) {
                            const diff = Math.abs(dayjs(validData[i]._time).diff(clickedTime));
                            if (diff < minDiff) {
                              minDiff = diff;
                              closestIdx = i;
                            }
                          }
                          
                          setDragState(prev => ({ ...prev, [ch]: { start: closestIdx, end: closestIdx, dragging: true } }));
                        }
                      }}
                      onMouseMove={(e) => {
                        const channelDrag = dragState[ch] || { start: null, end: null, dragging: false };
                        if (channelDrag.dragging && e && e.activeLabel && validData.length > 0) {
                          // Find the closest data point to the current mouse position
                          const currentTime = dayjs(e.activeLabel);
                          let closestIdx = 0;
                          let minDiff = Math.abs(dayjs(validData[0]._time).diff(currentTime));
                          
                          for (let i = 1; i < validData.length; i++) {
                            const diff = Math.abs(dayjs(validData[i]._time).diff(currentTime));
                            if (diff < minDiff) {
                              minDiff = diff;
                              closestIdx = i;
                            }
                          }
                          
                          setDragState(prev => ({ ...prev, [ch]: { ...prev[ch], end: closestIdx } }));
                        }
                      }}
                      onMouseUp={() => {
                        const channelDrag = dragState[ch] || { start: null, end: null, dragging: false };
                        if (channelDrag.dragging && channelDrag.start !== null && channelDrag.end !== null && channelDrag.start !== channelDrag.end) {
                          const startIdx = Math.min(channelDrag.start, channelDrag.end);
                          const endIdx = Math.max(channelDrag.start, channelDrag.end);
                          const startTime = validData[startIdx]?._time;
                          const endTime = validData[endIdx]?._time;
                          if (startTime && endTime) {
                            const newStart = dayjs(startTime);
                            const newEnd = dayjs(endTime);
                            setChannelTimeWindows(prev => ({
                              ...prev,
                              [ch]: { start: newStart, end: newEnd }
                            }));
                            fetchChannelData(newStart, newEnd, undefined, [ch], 'aggregated');
                          }
                        }
                        setDragState(prev => ({ ...prev, [ch]: { start: null, end: null, dragging: false } }));
                      }}
                      style={{ 
                        userSelect: (dragState[ch]?.dragging) ? 'none' : 'auto',
                        WebkitUserSelect: (dragState[ch]?.dragging) ? 'none' : 'auto',
                        MozUserSelect: (dragState[ch]?.dragging) ? 'none' : 'auto',
                        msUserSelect: (dragState[ch]?.dragging) ? 'none' : 'auto'
                      }}
                    >
                      <CartesianGrid strokeDasharray="0" stroke="#e0e0e0" opacity={0.7} />
                      <XAxis
                        dataKey="_time"
                        stroke="#666"
                        fontSize={11}
                        tick={{ fill: '#666' }}
                        tickLine={{ stroke: '#666' }}
                        axisLine={{ stroke: '#666' }}
                        angle={-45}
                        textAnchor="end"
                        height={40}
                        ticks={ticks}
                        minTickGap={10}
                        tickFormatter={v => dayjs(v).format(tickFormat)}
                      />
                      <YAxis
                        stroke="#666"
                        fontSize={11}
                        tick={{ fill: '#666' }}
                        tickLine={{ stroke: '#666' }}
                        axisLine={{ stroke: '#666' }}
                        width={expandedUnivariateGraphs[ch] ? 60 : 40}
                        domain={(() => {
                          const yValues = validData.map(d => d._value).filter((v): v is number => v !== null && !isNaN(v));
                          if (!yValues.length) return [0, 1];
                          const min = Math.min(...yValues);
                          const max = Math.max(...yValues);
                          if (min === max) return [min - 1, max + 1];
                          return [min, max];
                        })()}
                        allowDataOverflow={true}
                        allowDecimals={true}
                      />
                      <Line
                        type="monotone"
                        dataKey="_value"
                        name={ch}
                        stroke={getColorForChannel(ch, idx)}
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                        connectNulls={true}
                      />
                      {hoveredPoint[ch] && (
                        <>
                          <ReferenceLine x={hoveredPoint[ch]!.x} stroke="#8884d8" strokeDasharray="3 3" />
                          <ReferenceLine y={hoveredPoint[ch]!.y} stroke="#8884d8" strokeDasharray="3 3" />
                        </>
                      )}
                      {(dragState[ch]?.dragging && dragState[ch]?.start !== null && dragState[ch]?.end !== null) && (
                        <ReferenceArea
                          x1={validData[Math.min(dragState[ch]!.start!, dragState[ch]!.end!)]?._time}
                          x2={validData[Math.max(dragState[ch]!.start!, dragState[ch]!.end!)]?._time}
                          stroke="#00adef"
                          strokeOpacity={0.3}
                          fill="#00adef"
                          fillOpacity={0.15}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              );
            })}
        </Box>
      )}

      {chartViewMode === 'combined' && (
        <Paper sx={{ p: 3, mb: 2, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0', width: '100%' }}>
          {/* Combined (consolidated) chart at the top */}
          <Box sx={{ mb: 3, userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}>
            {/* Header with title and zoom controls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}>All Channels (Normalized Trends)</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Tooltip title="Zoom In">
                  <IconButton size="small" onClick={() => {
                    const mid = currentTimeWindow.start.add(currentTimeWindow.end.diff(currentTimeWindow.start) / 2);
                    const half = currentTimeWindow.end.diff(currentTimeWindow.start) / 4;
                    const newStart = mid.subtract(half);
                    const newEnd = mid.add(half);
                    fetchChannelData(newStart, newEnd, undefined, selectedChannels, 'aggregated');
                  }}>
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Zoom Out">
                  <IconButton size="small" onClick={() => {
                    const mid = currentTimeWindow.start.add(currentTimeWindow.end.diff(currentTimeWindow.start) / 2);
                    const full = currentTimeWindow.end.diff(currentTimeWindow.start);
                    const newStart = mid.subtract(full);
                    const newEnd = mid.add(full);
                    fetchChannelData(newStart, newEnd, undefined, selectedChannels, 'aggregated');
                  }}>
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset View">
                  <IconButton size="small" onClick={() => {
                    const selectedQuickRange = QUICK_RANGES.find(r => r.label === quickRangeLabel);
                    let newStart = dayjs().subtract(5, 'minute');
                    let newEnd = dayjs();
                    if (selectedQuickRange && selectedQuickRange.value) {
                      newStart = selectedQuickRange.value.start();
                      newEnd = selectedQuickRange.value.end();
                    }
                    fetchChannelData(newStart, newEnd, undefined, selectedChannels, 'aggregated');
                  }}>
                    <ReplayIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            {/* Channel selection checkboxes and Y-axis toggles */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              {selectedChannels
                .filter(ch => {
                  const data = channelDataMap[ch] || [];
                  const values = data
                    .filter(row => {
                      const t = dayjs(row._time);
                      return t.isAfter(currentTimeWindow.start) && t.isBefore(currentTimeWindow.end);
                    })
                    .map(row => parseFloat(row._value))
                    .filter(v => !isNaN(v));
                  return values.length > 0;
                })
                .map((ch: string, idx: number) => (
                  <Box key={ch} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox
                            checked={visibleChannels.includes(ch)}
                            onChange={(e) => {
                              e.stopPropagation();
                              if (visibleChannels.includes(ch)) {
                                setVisibleChannels(visibleChannels.filter(c => c !== ch));
                              } else {
                                setVisibleChannels([...visibleChannels, ch]);
                              }
                            }}
                            size="small"
                            sx={{ 
                              color: '#fff', 
                              '&.Mui-checked': { color: '#fff' },
                              p: 0,
                              mr: 0.5
                            }}
                          />
                          {ch}
                        </Box>
                      }
                      onClick={() => setHighlightedChannel(highlightedChannel === ch ? null : ch)}
                      sx={{
                        bgcolor: getColorForChannel(ch, idx),
                        color: '#fff',
                        fontWeight: 700,
                        cursor: 'pointer',
                        border: highlightedChannel === ch ? '2px solid #fff' : '2px solid transparent',
                        opacity: visibleChannels.includes(ch) ? 1 : 0.5,
                        '&:hover': {
                          opacity: 0.8
                        }
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showYAxis[ch] || false}
                          onChange={(e) => {
                            e.stopPropagation();
                            setShowYAxis(prev => ({
                              ...prev,
                              [ch]: e.target.checked
                            }));
                          }}
                          size="small"
                          sx={{ 
                            color: getColorForChannel(ch, idx),
                            '&.Mui-checked': { color: getColorForChannel(ch, idx) }
                          }}
                        />
                      }
                      label="Y"
                      sx={{ 
                        ml: 0.5,
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          color: getColorForChannel(ch, idx),
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          MozUserSelect: 'none',
                          msUserSelect: 'none'
                        }
                      }}
                    />
                  </Box>
                ))}
            </Box>
            
            <ResponsiveContainer width="100%" height={500}>
              <LineChart
                key={`combined-${lastPaintTime}`}
                data={(() => {
                  const allTimestamps = Array.from(new Set(
                    selectedChannels.flatMap(ch => (channelDataMap[ch] || []).map(row => row._time))
                  )).sort();
                  // Calculate min/max for each channel for normalization
                  const channelRanges: Record<string, { min: number, max: number }> = {};
                  selectedChannels.forEach(ch => {
                    const data = channelDataMap[ch] || [];
                    const values = data
                      .filter(row => {
                        const t = dayjs(row._time);
                        return t.isAfter(currentTimeWindow.start) && t.isBefore(currentTimeWindow.end);
                      })
                      .map(row => parseFloat(row._value))
                      .filter(v => !isNaN(v));
                    if (values.length > 0) {
                      const min = Math.min(...values);
                      const max = Math.max(...values);
                      channelRanges[ch] = { min, max };
                    }
                  });
                  return allTimestamps.map(ts => {
                    const obj: any = { _time: ts };
                    selectedChannels.forEach(ch => {
                      const found = (channelDataMap[ch] || []).find(row => row._time === ts);
                      if (found) {
                        const value = parseFloat(found._value);
                        const range = channelRanges[ch];
                        if (range && !isNaN(value)) {
                          // Normalize to 0-1 range
                          const normalized = (value - range.min) / (range.max - range.min);
                          obj[ch] = normalized;
                        } else {
                          obj[ch] = null;
                        }
                      } else {
                        obj[ch] = null;
                      }
                    });
                    return obj;
                  }).filter(row => {
                    const t = dayjs(row._time);
                    return t.isAfter(currentTimeWindow.start) && t.isBefore(currentTimeWindow.end);
                  });
                })()}
                margin={{ top: 20, right: 20, left: 20, bottom: 60 }}
                onMouseDown={(e) => {
                  if (e && e.activeLabel) {
                    const allData = (() => {
                      const allTimestamps = Array.from(new Set(
                        selectedChannels.flatMap(ch => (channelDataMap[ch] || []).map(row => row._time))
                      )).sort();
                      return allTimestamps.filter(ts => {
                        const t = dayjs(ts);
                        return t.isAfter(currentTimeWindow.start) && t.isBefore(currentTimeWindow.end);
                      });
                    })();
                    
                    // Find the closest data point to the clicked position
                    const clickedTime = dayjs(e.activeLabel);
                    let closestIdx = 0;
                    let minDiff = Math.abs(dayjs(allData[0]).diff(clickedTime));
                    
                    for (let i = 1; i < allData.length; i++) {
                      const diff = Math.abs(dayjs(allData[i]).diff(clickedTime));
                      if (diff < minDiff) {
                        minDiff = diff;
                        closestIdx = i;
                      }
                    }
                    
                    setDragState(prev => ({ ...prev, 'combined': { start: closestIdx, end: closestIdx, dragging: true } }));
                  }
                }}
                onMouseMove={(e) => {
                  const combinedDrag = dragState['combined'] || { start: null, end: null, dragging: false };
                  if (combinedDrag.dragging && e && e.activeLabel) {
                    const allData = (() => {
                      const allTimestamps = Array.from(new Set(
                        selectedChannels.flatMap(ch => (channelDataMap[ch] || []).map(row => row._time))
                      )).sort();
                      return allTimestamps.filter(ts => {
                        const t = dayjs(ts);
                        return t.isAfter(currentTimeWindow.start) && t.isBefore(currentTimeWindow.end);
                      });
                    })();
                    
                    // Find the closest data point to the current mouse position
                    const currentTime = dayjs(e.activeLabel);
                    let closestIdx = 0;
                    let minDiff = Math.abs(dayjs(allData[0]).diff(currentTime));
                    
                    for (let i = 1; i < allData.length; i++) {
                      const diff = Math.abs(dayjs(allData[i]).diff(currentTime));
                      if (diff < minDiff) {
                        minDiff = diff;
                        closestIdx = i;
                      }
                    }
                    
                    setDragState(prev => ({ ...prev, 'combined': { ...prev['combined'], end: closestIdx } }));
                  }
                }}
                onMouseUp={() => {
                  const combinedDrag = dragState['combined'] || { start: null, end: null, dragging: false };
                  if (combinedDrag.dragging && combinedDrag.start !== null && combinedDrag.end !== null && combinedDrag.start !== combinedDrag.end) {
                    const allData = (() => {
                      const allTimestamps = Array.from(new Set(
                        selectedChannels.flatMap(ch => (channelDataMap[ch] || []).map(row => row._time))
                      )).sort();
                      return allTimestamps.filter(ts => {
                        const t = dayjs(ts);
                        return t.isAfter(currentTimeWindow.start) && t.isBefore(currentTimeWindow.end);
                      });
                    })();
                    const startIdx = Math.min(combinedDrag.start, combinedDrag.end);
                    const endIdx = Math.max(combinedDrag.start, combinedDrag.end);
                    const startTime = allData[startIdx];
                    const endTime = allData[endIdx];
                    if (startTime && endTime) {
                      fetchChannelData(dayjs(startTime), dayjs(endTime), undefined, selectedChannels, 'aggregated');
                    }
                  }
                  setDragState(prev => ({ ...prev, 'combined': { start: null, end: null, dragging: false } }));
                }}
                style={{ 
                  userSelect: (dragState['combined']?.dragging) ? 'none' : 'auto',
                  WebkitUserSelect: (dragState['combined']?.dragging) ? 'none' : 'auto',
                  MozUserSelect: (dragState['combined']?.dragging) ? 'none' : 'auto',
                  msUserSelect: (dragState['combined']?.dragging) ? 'none' : 'auto'
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
                {highlightedChannel && showYAxis[highlightedChannel] && visibleChannels.includes(highlightedChannel) && (
                  <ReferenceLine
                    x={0}
                    stroke={getColorForChannel(highlightedChannel, selectedChannels.indexOf(highlightedChannel))}
                    strokeWidth={3}
                    strokeDasharray="5 5"
                    label={{
                      value: highlightedChannel,
                      position: 'insideLeft',
                      fill: getColorForChannel(highlightedChannel, selectedChannels.indexOf(highlightedChannel)),
                      fontWeight: 700,
                      fontSize: 12,
                      offset: 10
                    }}
                  />
                )}
                {(() => {
                  const allTimestamps = Array.from(new Set(
                    selectedChannels.flatMap(ch => (channelDataMap[ch] || []).map(row => row._time))
                  )).sort();
                  const data = allTimestamps.filter(ts => {
                    const t = dayjs(ts);
                    return t.isAfter(currentTimeWindow.start) && t.isBefore(currentTimeWindow.end);
                  });
                  let ticks = data.length <= 10 ? data : Array.from({ length: 10 }, (_, i) => {
                    const idx = Math.round(i * (data.length - 1) / 9);
                    return data[idx];
                  });
                  
                  // In live mode, ensure the last tick shows the current time
                  if (isLiveMode && ticks.length > 0) {
                    const currentTime = dayjs().toISOString();
                    // Replace the last tick with current time if it's more recent
                    const lastTick = dayjs(ticks[ticks.length - 1]);
                    const now = dayjs();
                    if (now.isAfter(lastTick)) {
                      ticks[ticks.length - 1] = currentTime;
                    }
                  }
                  
                  return (
                    <XAxis
                      dataKey="_time"
                      stroke="#666"
                      fontSize={11}
                      tick={{ fill: '#666' }}
                      tickLine={{ stroke: '#666' }}
                      axisLine={{ stroke: '#666' }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      minTickGap={10}
                      ticks={ticks}
                      tickFormatter={getTickFormatterWithDayTransitions(ticks, currentTimeWindow.start, currentTimeWindow.end, zoomLevel)}
                    />
                  );
                })()}
                <RechartsTooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                  labelStyle={{ fontWeight: 'bold', color: '#333' }}
                  itemStyle={{ color: '#666' }}
                  cursor={false}
                  wrapperStyle={{ pointerEvents: 'none' }}
                />
                {selectedChannels
                  .filter(ch => visibleChannels.includes(ch))
                  .map((ch: string, idx: number) => (
                    <Line
                      key={ch}
                      type="monotone"
                      dataKey={ch}
                      name={ch}
                      stroke={getColorForChannel(ch, idx)}
                      strokeWidth={highlightedChannel === ch ? 3 : 1.5}
                      dot={false}
                      isAnimationActive={false}
                      connectNulls={true}
                      opacity={highlightedChannel === ch ? 1 : 0.7}
                    />
                  ))}
                {(dragState['combined']?.dragging && dragState['combined']?.start !== null && dragState['combined']?.end !== null) && (() => {
                  const allData = (() => {
                    const allTimestamps = Array.from(new Set(
                      selectedChannels.flatMap(ch => (channelDataMap[ch] || []).map(row => row._time))
                    )).sort();
                    return allTimestamps.filter(ts => {
                      const t = dayjs(ts);
                      return t.isAfter(currentTimeWindow.start) && t.isBefore(currentTimeWindow.end);
                    });
                  })();
                  const startIdx = Math.min(dragState['combined']!.start!, dragState['combined']!.end!);
                  const endIdx = Math.max(dragState['combined']!.start!, dragState['combined']!.end!);
                  return (
                    <ReferenceArea
                      x1={allData[startIdx]}
                      x2={allData[endIdx]}
                      stroke="#00adef"
                      strokeOpacity={0.3}
                      fill="#00adef"
                      fillOpacity={0.15}
                    />
                  );
                })()}
              </LineChart>
            </ResponsiveContainer>
          </Box>
          {/* Stacked individual charts below */}
          <Box>
            {selectedChannels
              .filter(ch => {
                const data = channelDataMap[ch] || [];
                const values = data
                  .filter(row => {
                    const t = dayjs(row._time);
                    return t.isAfter(currentTimeWindow.start) && t.isBefore(currentTimeWindow.end);
                  })
                  .map(row => parseFloat(row._value))
                  .filter(v => !isNaN(v));
                return values.length > 0;
              })
              .map((ch, idx) => {
                const channelDataForChart = channelDataMap[ch] || [];
                let validData: ChartDataPoint[] = channelDataForChart.map((row, index) => {
                  const time = dayjs(row._time);
                  return {
                    _binned: time.toISOString(),
                    _value: parseFloat(row._value),
                    _time: row._time,
                    isRecent: isLiveMode && index >= channelDataForChart.length - 10
                  };
                });
                // During auto-refresh, use currentTimeWindow for both display and filtering
                // Otherwise use individual channel time window
                const windowStart = isLiveMode ? currentTimeWindow.start : (channelTimeWindows[ch] || currentTimeWindow).start;
                const windowEnd = isLiveMode ? currentTimeWindow.end : (channelTimeWindows[ch] || currentTimeWindow).end;
                validData = validData.filter(d => {
                  const t = dayjs(d._time);
                  return t.isAfter(windowStart) && t.isBefore(windowEnd);
                });
                
                // --- Combined chart dummy point logic ---
                if (isLiveMode) {
                  const nowISO = dayjs().toISOString();
                  if (!validData.some(d => d._time === nowISO)) {
                    validData = [
                      ...validData,
                      {
                        _binned: nowISO,
                        _value: null,
                        _time: nowISO,
                        isRecent: false,
                        _paintTime: lastPaintTime
                      } as ChartDataPoint
                    ];
                  }
                  validData = validData.map(d => ({ ...d, _paintTime: lastPaintTime }));
                }
                

                // Calculate X-axis ticks from filtered data
                const filteredTimestamps = validData.map(d => d._time).sort();
                let ticks = filteredTimestamps.length <= 10 ? filteredTimestamps : Array.from({ length: 10 }, (_, i) => {
                  const idx = Math.round(i * (filteredTimestamps.length - 1) / 9);
                  return filteredTimestamps[idx];
                });
                
                // In live mode, ensure the last tick shows the current time
                if (isLiveMode && ticks.length > 0) {
                  const currentTime = dayjs().toISOString();
                  // Replace the last tick with current time if it's more recent
                  const lastTick = dayjs(ticks[ticks.length - 1]);
                  const now = dayjs();
                  if (now.isAfter(lastTick)) {
                    ticks[ticks.length - 1] = currentTime;
                  }
                }
                const channelZoom = channelZoomLevels[ch] || 24;
                const tickFormat = getTickFormat(windowStart, windowEnd, channelZoom);
                return (
                  <Box key={ch} sx={{ mb: 1, background: '#fff', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e0e0e0', p: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: getColorForChannel(ch, idx) }}>{ch}</Typography>
                    </Box>
                    <ResponsiveContainer width="100%" height={120}>
                      <LineChart
                        data={validData}
                        margin={{ top: 10, right: 20, left: 40, bottom: 20 }}
                        style={{ 
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          MozUserSelect: 'none',
                          msUserSelect: 'none'
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.3} />
                        <XAxis
                          dataKey="_time"
                          stroke="#666"
                          fontSize={11}
                          tick={{ fill: '#666' }}
                          tickLine={{ stroke: '#666' }}
                          axisLine={{ stroke: '#666' }}
                          angle={-45}
                          textAnchor="end"
                          height={40}
                          ticks={ticks}
                          minTickGap={10}
                          tickFormatter={v => dayjs(v).format(tickFormat)}
                        />
                        <YAxis
                          stroke="#666"
                          fontSize={11}
                          tick={{ fill: '#666' }}
                          tickLine={{ stroke: '#666' }}
                          axisLine={{ stroke: '#666' }}
                          width={40}
                          domain={(() => {
                            const yValues = validData.map(d => d._value).filter((v): v is number => v !== null && !isNaN(v));
                            if (!yValues.length) return [0, 1];
                            const min = Math.min(...yValues);
                            const max = Math.max(...yValues);
                            if (min === max) return [min - 1, max + 1];
                            return [min, max];
                          })()}
                          allowDataOverflow={true}
                          allowDecimals={true}
                        />
                        <Line
                          type="monotone"
                          dataKey="_value"
                          name={ch}
                          stroke={getColorForChannel(ch, idx)}
                          strokeWidth={2}
                          dot={false}
                          isAnimationActive={false}
                          connectNulls={true}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                );
              })}
          </Box>
        </Paper>
      )}
    </Box>
  );
} 